import { QueryClient, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { format } from 'date-fns-tz';
import { ApiResponse } from 'src/constants/apiResponse';
import { HavePaginationEntity } from 'src/entities/HavePagination.entity';
import { OrderResponseEntity } from 'src/entities/Order.response.entity';
import { OrderEntity, OrderRequestEntity } from 'src/entities/orderEntity';
import { OrderSplitEntity } from 'src/entities/orderSplitEntity';

import { queryKey as useOrdersKey } from './useOrders.request';
import { usePlanningsDeliveriesCache, usePlanningsOrdersCache } from './usePlanningsCache';

const getOrders = (queryClient: QueryClient, startOn: Date, page: number) => async () => {
  const pathParam = format(startOn, 'yyyy-MM-dd', { timeZone: 'Asia/Tokyo' });
  const res = await axios.get<ApiResponse>(`/api/v2/orders?with_data=1&start_at_range_start_on=${pathParam}`);
  const list = res.data.data;
  return list.map((entity: OrderEntity) => {
    queryClient.setQueryData(['orders', { id: entity.id }], entity);
    return entity;
  });
};

const getOrder = (id: number) => async () => {
  const requestPath = `/api/v2/orders/${id}`;
  const res = await axios.get<OrderEntity>(requestPath);
  const entity = res.data;
  return entity;
};

export const useQueryOrders = (startOn: Date, page: number) => {
  const queryClient = useQueryClient();
  return useQuery(['orders', { page, startOn }], getOrders(queryClient, startOn, page), {
    staleTime: Infinity,
  });
};

export const useQueryOrder = (id: number) => useQuery(['orders', { id }], getOrder(id), {
    staleTime: Infinity,
    enabled: Boolean(id)
  });

export const useMutationOrder = () => {
  const queryClient = useQueryClient();
  const deliveriesCache = usePlanningsDeliveriesCache();
  const ordersCache = usePlanningsOrdersCache();

  return {
    addOrder: useMutation(
      (order: OrderRequestEntity) => axios.post('/api/v2/orders', { order }),
      {
        onSuccess: (res: AxiosResponse<OrderEntity>) => {
          ordersCache.addOrders([res.data]);
          void queryClient.invalidateQueries({ queryKey: ['dashboards'] });
        },
        onError: (error: AxiosError<{data: string[]}>) => error.response.data
      }
    ),
    updateOrder: useMutation(
      (order: OrderEntity) => axios.put(`/api/v2/orders/${order.id}`, { order }),
      {
        onSuccess: (res) => {
          const order = res.data as OrderEntity;
          ordersCache.update([order]);
          const queries = queryClient.getQueryCache().findAll({ predicate: (query) => query.queryKey.includes(useOrdersKey) });
          queries.forEach((query) => {
            queryClient.setQueryData(query.queryKey, (oldData: HavePaginationEntity<OrderResponseEntity>) => {
              if (oldData && oldData.data) {
                return {
                  ...oldData,
                  data: oldData.data.map((it) => {
                    if (it.id === order.id) {
                      return {
                        ...it,
                        shipper_name: order.shipper_name,
                        shipper_contact_person: order.shipper_contact_person,
                        shipper_email_address: order.shipper_email_address,
                        shipper_phone_number: order.shipper_phone_number,
                        code: order.code,
                        memo: order.memo,
                        designated_truck_car_models: order.designated_truck_car_models,
                        designated_truck_features: order.designated_truck_features,
                        designated_truck_floor_specifications: order.designated_truck_floor_specifications,
                        designated_truck_loading_platform_heights: order.designated_truck_loading_platform_heights,
                        designated_truck_loading_platform_lengths: order.designated_truck_loading_platform_lengths,
                        designated_truck_loading_platform_widths: order.designated_truck_loading_platform_widths,
                        designated_truck_klasses: order.designated_truck_klasses,
                        item_count: order.item_count,
                        item_can_be_mixed: order.item_can_be_mixed,
                        item_handling_of_cargo_style: order.item_handling_of_cargo_style,
                        item_klass: order.item_klass,
                        item_name: order.item_name,
                        item_packing_style: order.item_packing_style,
                        loading_name: order.loading_name,
                        loading_address: order.loading_address,
                        loading_staying_seconds: order.loading_staying_seconds,
                        loading_staying_minutes: order.loading_staying_minutes,
                        unloading_name: order.unloading_name,
                        unloading_address: order.unloading_address,
                        unloading_staying_seconds: order.unloading_staying_seconds,
                        unloading_staying_minutes: order.unloading_staying_minutes,
                        unit: order.unit,
                        loading_latitude: order.loading_latitude,
                        loading_longitude: order.loading_longitude,
                        unloading_latitude: order.unloading_latitude,
                        unloading_longitude: order.unloading_longitude,
                        order_loading_specified_json: JSON.stringify(order.order_unloading_specified_datetimes_attributes),
                        order_unloading_specified_json: JSON.stringify(order.order_unloading_specified_datetimes_attributes),
                        custom_input_fields: order.custom_input_fields,
                        item_height_mm: order.item_height_cm ? order.item_height_cm * 10 : order.item_height_cm,
                        item_total_volume_mm3: order.item_total_volume_m3 ? order.item_total_volume_m3 * 1000 : order.item_total_volume_m3,
                        item_total_weight_for_calculation: order.item_total_weight_kg ? order.item_total_weight_kg * 1000 : order.item_total_weight_kg,
                        item_vertical_width_mm: order.item_vertical_width_cm ? order.item_vertical_width_cm * 10 : order.item_vertical_width_cm,
                        item_width_mm: order.item_width_cm ? order.item_width_cm * 10 : order.item_width_cm,
                        // allowed_trucks_json: JSON.stringify(order.allowed_trucks),
                        // denied_drivers_json: JSON.stringify(order.denied_drivers),
                        order_operations_count: order.order_operations_count,
                        // delivery_id?: number;
                        // drivers_json?: string;
                        // truck_json?: string;
                        order_billing_amount_json: JSON.stringify(order.order_billing_amount_attributes),
                        order_payment_amount_json: JSON.stringify(order.order_payment_amount_attributes),
                        order_assignments_json: JSON.stringify(order.order_assignment_attributes),
                      };
                    }
                    return it;
                  }),
                };
              }
              return oldData;
            });
          });
          void queryClient.invalidateQueries({ queryKey: ['dashboards'] });
        },
        onError: (error: AxiosError<{data: string[]}>) => error.response.data
      }
    ),
    deleteOrder: useMutation(
      (id: number) => axios.delete(`/api/v2/orders/${id}`),
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: ['orders'] });
          void queryClient.invalidateQueries({ queryKey: ['dashboards'] });
        },
        onError: (error: AxiosError<{data: string[]}>) => error.response.data
      }
    ),
    deleteOrders: useMutation(
      (ids: number[]) => axios.delete('/api/v2/orders/bulk', { data: { ids } }),
      {
        onSuccess: (_res, variables) => {
          ordersCache.removeOrderByIds(variables);
          void queryClient.invalidateQueries({ queryKey: ['dashboards'] });
        },
        onError: (error: AxiosError<{data: string[]}>) => error.response.data
      }
    ),
    restoreSplittedOrder: useMutation(
      (id: number) => axios.delete(`/api/v4/master/orders/${id}/order_split`),
      {
        onSuccess: (res: AxiosResponse<{ order: OrderEntity, siblingOrderIds: number[] }>) => {
          const { order, siblingOrderIds } = res.data;
          ordersCache.removeOrderByIds(siblingOrderIds);
          ordersCache.update([order]);
          void queryClient.invalidateQueries({ queryKey: ['dashboards'] });
        },
        onError: (error: AxiosError<{data: string[]}>) => error.response.data
      }
    ),
    restoreSplittedOrders: useMutation(
      (dates: { startOn: string, endOn: string }) => axios.post(`/api/v4/master/delete_all/order_splits?start_on=${dates.startOn}&end_on=${dates.endOn}`),
      {
        onSuccess: (res: AxiosResponse<{ orders: OrderEntity[], siblingOrderIds: number[] }>) => {
          const { orders, siblingOrderIds } = res.data;
          ordersCache.removeOrderByIds(siblingOrderIds);
          ordersCache.update(orders);
          void queryClient.invalidateQueries({ queryKey: ['dashboards'] });
        },
        onError: (error: AxiosError<{data: string[]}>) => error.response.data
      }
    ),
    deleteOperations: useMutation(
      (id: number) => axios.delete(`/api/v2/orders/${id}/order_operations`)
    ),
    splitOrder: useMutation(
      (entity: OrderSplitEntity) => axios.post(`/api/v2/orders/${entity.order_id}/split`, { data: entity.data }),
      {
        onSuccess: (res: AxiosResponse<{ order: OrderEntity, splitOrders: OrderEntity[] }>) => {
          ordersCache.update([res.data.order]);
          ordersCache.addOrders(res.data.splitOrders);

          void queryClient.invalidateQueries({ queryKey: ['dashboards'] });
        },
        onError: (error: AxiosError<{data: string[]}>) => error.response.data
      }
    ),
    operationBulkResetting: useMutation(
      (orderIds: number[]) => axios.post('/api/v3/orders/order_operations/bulk/resetting', { order_ids: orderIds }),
      {
        onSuccess: (_data, variant) => {
          deliveriesCache.removeOrderIds(variant);
          ordersCache.removeAssignments(variant);

          void queryClient.invalidateQueries({ queryKey: ['dashboards'] });
        }
      }
    ),
  };
};

const fetchUnallocatedOrderIds = (queryClient: QueryClient) => async ({ pageParam = 1, queryKey }) => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  const startOn = queryKey[1] as string;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  const endOn = queryKey[2] as string;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  const perPage = queryKey[3] as number;
  const requestPath = `/api/v2/orders?with_data=1&start_at_range_start_on=${startOn}&start_at_range_end_on=${endOn}&page=${pageParam}&not_planed=true&per_page=${perPage}`;

  const response: AxiosResponse<{
    ids: number[],
    total_pages_count: number;
    data: OrderEntity[];
  }> = await axios.get(requestPath).then((res) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const list = res.data.data as OrderEntity[];
    list.map((entity: OrderEntity) => {
      queryClient.setQueryData(['orders', { id: entity.id }], entity);
      return entity;
    });
    return res;
  });
  return response.data;
};

const fetchOrderIds = (queryClient: QueryClient) => async ({ pageParam = 1, queryKey }) => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  const startOn = queryKey[1] as string;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  const endOn = queryKey[2] as string;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  const perPage = queryKey[3] as number;
  const requestPath = `/api/v2/orders?with_data=1&start_at_range_start_on=${startOn}&start_at_range_end_on=${endOn}&page=${pageParam}&per_page=${perPage}`;

  const response: AxiosResponse<{
    ids: number[],
    total_pages_count: number;
    data: OrderEntity[];
  }> = await axios.get(requestPath).then((res) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const list = res.data.data as OrderEntity[];
    list.map((entity: OrderEntity) => {
      queryClient.setQueryData(['orders', { id: entity.id }], entity);
      return entity;
    });
    return res;
  });
  return response.data;
};

export const useInfiniteQueryUnallocatedOrders = (startDate: string, endDate: string, perPage: number) => {
  const queryClient = useQueryClient();
  return useInfiniteQuery(
    ['orders', startDate, endDate, perPage],
    fetchUnallocatedOrderIds(queryClient),
    {
      enabled: !!startDate && !!endDate,
      staleTime: Infinity,
      getNextPageParam: (lastPage, allPages) => (allPages.length < lastPage.total_pages_count ? allPages.length + 1 : undefined),
    }
  );
};

export const useInfiniteQueryOrders = (startDate: string, endDate: string, perPage: number) => {
  const queryClient = useQueryClient();
  return useInfiniteQuery(
    ['orders', startDate, endDate, perPage],
    fetchOrderIds(queryClient),
    {
      enabled: !!startDate && !!endDate,
      staleTime: Infinity,
      getNextPageParam: (lastPage, allPages) => (allPages.length < lastPage.total_pages_count ? allPages.length + 1 : undefined),
    }
  );
};
